import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { Download, Trash2, Play, Pause, RefreshCw, AlertCircle } from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'
import { formatDistanceToNow, format } from 'date-fns'
import { NSModal, NSField, NSSelect, NSSegment, NSToggle } from '../shared/ns-kit'
import { I, Icon } from '../shared/ns-icons'

// ── Backup schedule modal (NS design, persists to /backups/schedule) ──────────
const SCOPE_ROWS = [
  { k: 'content', l: 'Page content & structure', icon: 'type' },
  { k: 'media', l: 'Media library', icon: 'image' },
  { k: 'database', l: 'Forms & database', icon: 'layers' },
  { k: 'settings', l: 'Site settings & domains', icon: 'settings' },
]
const RUN_AT_OPTIONS = ['00:00', '01:00', '02:00', '03:00', '04:00', '06:00']
const TZ_OPTIONS = ['UTC', 'America/New_York', 'Europe/London', 'Asia/Tokyo', 'Australia/Sydney']

function BackupScheduleModal({ onClose, onSaved }) {
  const queryClient = useQueryClient()
  const { data } = useQuery(['backup-schedule'], () => apiService.get('/backups/schedule').then(r => r.data), { staleTime: 0 })
  const cfg = data?.schedule

  const [freq, setFreq] = useState('Daily')
  const [runAt, setRunAt] = useState('02:00')
  const [timezone, setTimezone] = useState('UTC')
  const [retention, setRetention] = useState('30 days')
  const [scope, setScope] = useState({ content: true, media: true, database: true, settings: true })
  const [notify, setNotify] = useState(true)
  const [hydrated, setHydrated] = useState(false)

  React.useEffect(() => {
    if (cfg && !hydrated) {
      setFreq(cfg.frequency || 'Daily')
      setRunAt(cfg.runAt || '02:00')
      setTimezone(cfg.timezone || 'UTC')
      setRetention(cfg.retention || '30 days')
      setScope({ content: true, media: true, database: true, settings: true, ...(cfg.scope || {}) })
      setNotify(cfg.notifyOnFailure !== false)
      setHydrated(true)
    }
  }, [cfg, hydrated])

  const toggle = (k) => setScope(s => ({ ...s, [k]: !s[k] }))

  const saveMutation = useMutation(
    (payload) => apiService.put('/backups/schedule', payload),
    {
      onSuccess: (res) => {
        toast.success('Backup schedule saved')
        // Seed the cache with the saved config so the modal reflects the new
        // values on next open (without a page refresh). The one-time hydrate
        // guard means invalidate-only wouldn't reliably re-apply fresh data.
        const saved = res?.data?.schedule
        if (saved) queryClient.setQueryData(['backup-schedule'], { schedule: saved })
        else queryClient.invalidateQueries(['backup-schedule'])
        onSaved?.()
        onClose()
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to save schedule'),
    }
  )

  const save = () => saveMutation.mutate({ frequency: freq, runAt, timezone, retention, scope, notifyOnFailure: notify })

  return (
    <NSModal
      icon="clock" tone="info" onClose={onClose} width={600}
      title="Backup schedule" subtitle="Applies to all client sites unless overridden per-site."
      foot={
        <>
          <button className="btn btn-ghost" style={{ marginRight: 'auto', color: 'var(--text-muted)' }} onClick={() => { setFreq('Daily'); setRunAt('02:00'); setTimezone('UTC'); setRetention('30 days'); setScope({ content: true, media: true, database: true, settings: true }); setNotify(true) }}>Reset to defaults</button>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saveMutation.isLoading}><I.check /> {saveMutation.isLoading ? 'Saving…' : 'Save schedule'}</button>
        </>
      }
    >
      <NSField label="Frequency">
        <NSSegment options={['Hourly', 'Daily', 'Weekly']} value={freq} onChange={setFreq} />
      </NSField>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <NSField label="Run at" hint={freq === 'Weekly' ? 'every Sunday' : freq === 'Hourly' ? 'on the hour' : 'daily'}>
          <NSSelect value={runAt} onChange={e => setRunAt(e.target.value)} options={RUN_AT_OPTIONS} />
        </NSField>
        <NSField label="Timezone">
          <NSSelect value={timezone} onChange={e => setTimezone(e.target.value)} options={TZ_OPTIONS} />
        </NSField>
      </div>

      <NSField label="Retention" hint="older snapshots are pruned automatically">
        <NSSegment options={['7 days', '30 days', '90 days', '1 year']} value={retention} onChange={setRetention} />
      </NSField>

      <NSField label="What to back up">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SCOPE_ROWS.map(r => (
            <div key={r.k} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface)' }}>
              <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 14 }}><Icon name={r.icon} /></span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{r.l}</span>
              <NSToggle on={!!scope[r.k]} onChange={() => toggle(r.k)} />
            </div>
          ))}
        </div>
      </NSField>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--border)', borderRadius: 11, background: 'var(--bg-2)' }}>
        <span className="icon-tile success" style={{ width: 34, height: 34, fontSize: 15 }}><I.shield /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500 }}>Notify on failure</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>Email the workspace owner if a backup fails twice in a row.</div>
        </div>
        <NSToggle on={notify} onChange={setNotify} />
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 7 }}>
        <I.clock /> {freq === 'Hourly' ? 'Runs every hour' : freq === 'Weekly' ? `Runs Sundays at ${runAt}` : `Runs daily at ${runAt}`} {timezone} · encrypted at rest
      </div>
    </NSModal>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STORAGE_LIMIT_GB = 200

const fmtBytes = (b) => {
  if (!b || b <= 0) return '0 MB'
  const mb = b / (1024 * 1024)
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(mb < 10 ? 1 : 0)} MB`
}

const initialsOf = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
const gradOf = (name) => {
  let h = 0
  for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return (h % 6) + 1
}
const relTime = (ts) => ts ? formatDistanceToNow(new Date(ts), { addSuffix: true }) : 'never'

// Compact "x ago" — e.g. "8h ago", "2d ago"
const compactAgo = (ts) => {
  if (!ts) return 'never'
  const diff = Date.now() - new Date(ts).getTime()
  if (diff < 60000) return 'just now'
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 60) return `${m}m ago`
  if (h < 48) return `${h}h ago`
  return `${d}d ago`
}

// Compact "in x" until a future time — e.g. "in 22h", "in 3d"
const compactUntil = (ts) => {
  if (!ts) return null
  const diff = new Date(ts).getTime() - Date.now()
  if (diff <= 0) return 'now'
  const m = Math.ceil(diff / 60000)
  const h = Math.round(diff / 3600000)
  const d = Math.round(diff / 86400000)
  if (h < 1) return `in ${m}m`
  if (h < 48) return `in ${h}h`
  return `in ${d}d`
}

// Format an absolute timestamp in the schedule's configured timezone (so the
// "next run" time lines up with the "Daily at 02:00 UTC" summary instead of
// the viewer's local time). No tz label — the schedule card already shows it.
const fmtInTz = (ts, tz) => {
  if (!ts) return 'N/A'
  try {
    return new Date(ts).toLocaleString('en-US', {
      timeZone: tz || 'UTC', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return format(new Date(ts), 'MMM d, HH:mm')
  }
}

const CLIENT_STATUS = {
  healthy: { label: 'Healthy', tone: 'success' },
  overdue: { label: 'Overdue', tone: 'warning' },
  failed:  { label: 'Failed',  tone: 'danger' },
}

const siteOf = (b) => {
  const s = b.metadata?.sites_included
  if (Array.isArray(s) && s.length) return s.length === 1 ? s[0].name : `${s[0].name} +${s.length - 1}`
  return `${b.sitesCount || 0} site${b.sitesCount === 1 ? '' : 's'}`
}
const typeOf = (b) => (b.metadata?.backup_type === 'scheduled' ? 'auto' : (b.metadata?.backup_type || 'manual'))

const RECENT_GRID = '1.2fr 1.3fr 1.5fr 0.9fr 0.8fr 1fr 92px'

// Tiny popover menu (outside-click close)
function RowMenu({ children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="site-action" onClick={() => setOpen(o => !o)} title="More"><I.more /></button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 40, minWidth: 170, background: 'var(--bg-1)', border: '1px solid var(--border-strong)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 6 }}>
          {children(setOpen)}
        </div>
      )}
    </div>
  )
}
const menuItem = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  padding: '8px 10px', borderRadius: 7, fontSize: 12.5, cursor: 'pointer',
  border: 'none', background: 'transparent', color: 'var(--text-soft)', fontFamily: 'var(--font)',
}

// ── Create backup modal ───────────────────────────────────────────────────────

function CreateBackupModal({ clients, presetClientId, onClose, onCreate, creating }) {
  const [clientId, setClientId] = useState(presetClientId || '')
  return (
    <NSModal
      icon="archive" tone="accent" onClose={onClose} width={520}
      title="Create backup" subtitle="Snapshot a client's sites, media and settings."
      foot={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!clientId || creating} onClick={() => onCreate(clientId)}>
            <I.archive /> {creating ? 'Creating…' : 'Create backup'}
          </button>
        </>
      }
    >
      <NSField label="Client">
        <NSSelect
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          options={[{ value: '', label: 'Choose a client…' }, ...clients.map(c => ({ value: c.id, label: c.company_name }))]}
        />
      </NSField>
    </NSModal>
  )
}

// ── Restore backup modal ──────────────────────────────────────────────────────

function RestoreBackupModal({ backup, onClose, onConfirm, restoring }) {
  return (
    <NSModal
      icon="upload" tone="warning" onClose={onClose} width={520}
      title="Restore backup" subtitle="Roll a client's sites back to this snapshot."
      foot={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" disabled={restoring} onClick={onConfirm}>
            <I.upload /> {restoring ? 'Restoring…' : 'Restore backup'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 11, padding: 12, borderRadius: 11, background: 'var(--danger-soft)', border: '1px solid var(--border)', marginBottom: 14 }}>
        <span className="icon-tile danger" style={{ width: 32, height: 32, fontSize: 15, flexShrink: 0 }}><I.bolt /></span>
        <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>
          Restoring overwrites the client's current site data. This can't be undone.
        </div>
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 11, padding: 12, fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="text-muted">File</span><span style={{ fontFamily: 'var(--font-mono)' }}>{backup.fileName}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="text-muted">Client</span><span>{backup.clientName}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="text-muted">Sites</span><span>{backup.sitesCount}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="text-muted">Size</span><span style={{ fontFamily: 'var(--font-mono)' }}>{backup.fileSize}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="text-muted">Created</span><span>{format(new Date(backup.createdAt), 'MMM d, yyyy HH:mm')}</span></div>
      </div>
    </NSModal>
  )
}

// ── Main dashboard ────────────────────────────────────────────────────────────

const AdminBackupDashboard = () => {
  const queryClient = useQueryClient()
  const [showSchedule, setShowSchedule] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [createPreset, setCreatePreset] = useState('')
  const [restoreTarget, setRestoreTarget] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [backupToDelete, setBackupToDelete] = useState(null)

  const { data: overview, isLoading, refetch: refetchOverview } = useQuery(
    ['backup-overview'],
    () => apiService.get('/backups/overview').then(r => r.data),
    { refetchInterval: 30000 }
  )

  const { data: recentData, isLoading: recentLoading, refetch: refetchRecent } = useQuery(
    ['backups-recent'],
    () => apiService.get('/backups?limit=12').then(r => r.data),
    { refetchInterval: 60000 }
  )

  const { data: clientsData } = useQuery(['clients'], () => apiService.get('/clients').then(r => r.data), { staleTime: 30000 })
  const clients = clientsData?.clients || []

  const refreshAll = () => { refetchOverview(); refetchRecent() }

  const triggerMutation = useMutation(() => apiService.post('/backups/scheduler/trigger'), {
    onSuccess: () => { toast.success('Backup started — running in the background'); setTimeout(refreshAll, 2500) },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to start backup'),
  })

  const createMutation = useMutation(
    (clientId) => apiService.post(`/backups/client/${clientId}`, { type: 'manual' }),
    {
      onSuccess: () => { toast.success('Backup created'); setShowCreate(false); setCreatePreset(''); refreshAll() },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to create backup'),
    }
  )

  const deleteMutation = useMutation((id) => apiService.delete(`/backups/${id}`), {
    onSuccess: () => { toast.success('Backup deleted'); refreshAll() },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete backup'),
  })

  const restoreMutation = useMutation((id) => apiService.post(`/backups/${id}/restore`), {
    onSuccess: () => { toast.success('Restore initiated'); setRestoreTarget(null) },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to restore backup'),
  })

  const startSchedulerMutation = useMutation(() => apiService.post('/backups/scheduler/start'), {
    onSuccess: () => { toast.success('Scheduler started'); refetchOverview() },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to start scheduler'),
  })
  const stopSchedulerMutation = useMutation(() => apiService.post('/backups/scheduler/stop'), {
    onSuccess: () => { toast.success('Scheduler stopped'); refetchOverview() },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to stop scheduler'),
  })

  const downloadBackup = async (b) => {
    try {
      const res = await apiService.get(`/backups/${b.id}/download`, { responseType: 'blob', headers: { Accept: 'application/zip' } })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
      const a = document.createElement('a')
      a.href = url
      a.download = (b.fileName || 'backup').replace(/[^a-zA-Z0-9.-]/g, '_')
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      toast.success('Backup downloaded')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to download backup')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '3rem 0' }}>
        <LoadingSpinner size="lg" text="Loading backup dashboard..." />
      </div>
    )
  }

  const stats = overview?.stats || {}
  const scheduler = overview?.scheduler || {}
  const breakdown = overview?.storageBreakdown || {}
  const clientRows = overview?.clients || []
  const recent = recentData?.backups || []

  const totalStorageMB = stats.totalStorageMB || 0
  const storageGB = totalStorageMB / 1024
  const storageLabel = storageGB >= 1 ? `${storageGB.toFixed(1)} GB` : `${Math.round(totalStorageMB)} MB`
  const storagePct = Math.min((storageGB / STORAGE_LIMIT_GB) * 100, 100)
  const errors = scheduler.stats?.errors || []
  const issueCount = clientRows.filter(c => c.status !== 'healthy').length

  const storageRows = [
    { l: 'Daily auto-backups', c: 'var(--accent)', bytes: breakdown.scheduled?.bytes || 0 },
    { l: 'Manual snapshots', c: 'var(--info)', bytes: breakdown.manual?.bytes || 0 },
    { l: 'Pre-publish points', c: 'var(--success)', bytes: breakdown.pre_publish?.bytes || 0 },
  ]

  const bento = [
    { label: 'Total backups', value: (stats.totalBackups || 0).toLocaleString(), sub: 'all time', icon: 'archive', tone: 'accent' },
    { label: 'Last 24h', value: stats.last24Hours || 0, sub: `across ${stats.clientsWithBackups || 0} clients`, icon: 'cloud', tone: 'info' },
    { label: 'Failed', value: stats.failedBackups || 0, sub: stats.failedBackups ? 'needs attention' : 'all healthy', icon: 'bolt', tone: stats.failedBackups ? 'danger' : 'success' },
    { label: 'Storage used', value: storageLabel, sub: `of ${STORAGE_LIMIT_GB} GB`, icon: 'layers', tone: 'success' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 4 }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Reliability</div>
          <h1>Backups</h1>
          <div className="subtitle">Automated daily snapshots across all client sites</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => { setCreatePreset(''); setShowCreate(true) }}><I.plus /> Create backup</button>
          <button className="btn" onClick={() => setShowSchedule(true)}><I.settings /> Schedule</button>
          <button className="btn btn-primary" onClick={() => triggerMutation.mutate()} disabled={triggerMutation.isLoading}>
            <I.cloud /> {triggerMutation.isLoading ? 'Running…' : 'Run backup now'}
          </button>
        </div>
      </div>

      <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Bento */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {bento.map(({ label, value, sub, icon, tone }) => (
            <div key={label} className="card card-pad-sm">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                <span className={`icon-tile ${tone}`} style={{ width: 28, height: 28, fontSize: 13 }}><Icon name={icon} /></span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Status by client + storage */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 14, alignItems: 'stretch' }}>
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 className="h2">Status by client</h2>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Last snapshot per account</div>
              </div>
              {issueCount > 0 && (
                <span className="badge badge-warning"><span className="badge-dot" /> {issueCount} {issueCount === 1 ? 'issue' : 'issues'}</span>
              )}
            </div>
            {clientRows.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {clientRows.map(c => {
                  const st = CLIENT_STATUS[c.status] || CLIENT_STATUS.overdue
                  return (
                    <div key={c.clientId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
                      <span className={`avatar avatar-grad-${gradOf(c.clientName)}`} style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>{initialsOf(c.clientName)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="truncate" style={{ fontSize: 13, fontWeight: 500 }}>{c.clientName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                          Last {compactAgo(c.lastBackupAt)} · {fmtBytes(c.lastSizeBytes)} · next {c.status === 'healthy' ? (compactUntil(overview?.nextRun) || 'scheduled') : 'overdue'}
                        </div>
                      </div>
                      <span className={`badge badge-${st.tone}`}><span className="badge-dot" /> {st.label}</span>
                      <RowMenu>
                        {(close) => (
                          <button style={menuItem} onClick={() => { setCreatePreset(c.clientId); setShowCreate(true); close() }}>
                            <I.archive /> Back up now
                          </button>
                        )}
                      </RowMenu>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <span className="icon-tile" style={{ width: 36, height: 36, fontSize: 16, margin: '0 auto 8px' }}><I.archive /></span>
                <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>No clients yet</p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="card card-pad" style={{ flex: 1 }}>
              <h2 className="h2">Storage</h2>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, marginBottom: 16 }}>Retained snapshots</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 10 }}>
                <span style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{storageLabel}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>of {STORAGE_LIMIT_GB} GB</span>
              </div>
              <div className="progress" style={{ height: 8 }}><div style={{ width: `${storagePct}%` }} /></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                {storageRows.map(r => (
                  <div key={r.l} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: r.c, flexShrink: 0 }} />
                    <span style={{ color: 'var(--text-soft)' }}>{r.l}</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtBytes(r.bytes)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className="icon-tile success" style={{ width: 42, height: 42, fontSize: 19, flexShrink: 0 }}><I.shield /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {scheduler.config?.frequency === 'Hourly'
                    ? 'Hourly'
                    : `${scheduler.config?.frequency || 'Daily'} at ${scheduler.config?.runAt || '02:00'} ${scheduler.config?.timezone || 'UTC'}`}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{scheduler.config?.retention || '30 days'} retention · encrypted at rest</div>
              </div>
              <button className="btn btn-sm" onClick={() => setShowSchedule(true)}>Edit</button>
              <span className={scheduler.isRunning ? 'badge badge-success' : 'badge badge-danger'}><span className="badge-dot" /> {scheduler.isRunning ? 'Active' : 'Stopped'}</span>
            </div>
          </div>
        </div>

        {/* Recent backups */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 className="h2">Recent backups</h2>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Latest backup files from all clients</div>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={refreshAll}><RefreshCw style={{ width: 13, height: 13 }} /> Refresh</button>
          </div>

          {recentLoading ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading backups…</div>
          ) : recent.length > 0 ? (
            <div className="sites-table" style={{ border: 'none', background: 'transparent' }}>
              <div className="row header" style={{ gridTemplateColumns: RECENT_GRID, paddingLeft: 0, paddingRight: 0 }}>
                <div>Time</div><div>Client</div><div>Site</div><div>Size</div><div>Type</div><div>Status</div><div></div>
              </div>
              {recent.map(b => (
                <div key={b.id} className="row" style={{ gridTemplateColumns: RECENT_GRID, paddingLeft: 0, paddingRight: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{format(new Date(b.createdAt), 'MMM d, HH:mm')}</div>
                  <div className="truncate" style={{ color: 'var(--text-soft)' }}>{b.clientName}</div>
                  <div className="truncate" style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-soft)' }}>{siteOf(b)}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{b.fileSize}</div>
                  <div><span className="badge" style={{ fontSize: 10 }}>{typeOf(b)}</span></div>
                  <div>
                    {b.status === 'completed'
                      ? <span className="badge badge-success" style={{ fontSize: 10 }}><I.check style={{ fontSize: 11 }} /> Success</span>
                      : <span className="badge badge-danger" style={{ fontSize: 10 }}><I.x style={{ fontSize: 11 }} /> Failed</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <button className="site-action" onClick={() => downloadBackup(b)} title="Download"><Download style={{ width: 14, height: 14 }} /></button>
                    <button className="site-action" onClick={() => setRestoreTarget(b)} disabled={b.status !== 'completed'} title="Restore"><I.upload /></button>
                    <button className="site-action" onClick={() => { setBackupToDelete(b); setShowDeleteConfirm(true) }} title="Delete" style={{ color: 'var(--danger)' }}><Trash2 style={{ width: 14, height: 14 }} /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <span className="icon-tile" style={{ width: 40, height: 40, fontSize: 18, margin: '0 auto 10px' }}><I.archive /></span>
              <p className="text-muted">No backups yet</p>
              <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 4 }}>They'll appear here once the scheduler runs or you create one.</p>
            </div>
          )}
        </div>

        {/* Scheduler status (kept for admin control) */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className={`icon-tile ${scheduler.isRunning ? 'success' : 'danger'}`} style={{ width: 38, height: 38, fontSize: 16 }}><I.clock /></span>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>Backup scheduler</span>
                  <span className={scheduler.isRunning ? 'badge badge-success' : 'badge badge-danger'}>{scheduler.isRunning ? 'Running' : 'Stopped'}</span>
                </div>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                  {scheduler.isRunning
                    ? `Automatic backups ${scheduler.config?.frequency?.toLowerCase() || 'daily'}${scheduler.config?.frequency === 'Hourly' ? '' : ` at ${scheduler.config?.runAt || '02:00'} ${scheduler.config?.timezone || 'UTC'}`}`
                    : 'Automatic backups are currently disabled'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={() => triggerMutation.mutate()} disabled={triggerMutation.isLoading}><RefreshCw style={{ width: 13, height: 13 }} /> Run now</button>
              {scheduler.isRunning ? (
                <button className="btn btn-sm" onClick={() => stopSchedulerMutation.mutate()} disabled={stopSchedulerMutation.isLoading}><Pause style={{ width: 13, height: 13 }} /> Stop</button>
              ) : (
                <button className="btn btn-sm btn-primary" onClick={() => startSchedulerMutation.mutate()} disabled={startSchedulerMutation.isLoading}><Play style={{ width: 13, height: 13 }} /> Start</button>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 18 }}>
            {[
              { label: 'Next run', value: scheduler.nextRun ? fmtInTz(scheduler.nextRun, scheduler.config?.timezone) : 'N/A' },
              { label: 'Last run', value: scheduler.lastRun ? relTime(scheduler.lastRun) : 'Never' },
              { label: 'Recent errors', value: errors.length, danger: true },
            ].map(({ label, value, danger }) => (
              <div key={label} style={{ textAlign: 'center', padding: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
                <div style={{ fontSize: 15.5, fontWeight: 600, fontFamily: 'var(--font-mono)', color: danger && errors.length ? 'var(--danger)' : 'var(--text)' }}>{value}</div>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Error log */}
        {errors.length > 0 && (
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <AlertCircle style={{ width: 18, height: 18, color: 'var(--danger)' }} />
              <h2 className="h2" style={{ margin: 0 }}>Recent errors</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {errors.map((err, i) => (
                <div key={i} style={{ padding: 12, background: 'var(--danger-soft)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12.5, color: 'var(--danger)' }}>
                  {typeof err === 'string' ? err : (err.message || JSON.stringify(err))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showSchedule && (
        <BackupScheduleModal onClose={() => setShowSchedule(false)} onSaved={() => refetchOverview()} />
      )}
      {showCreate && (
        <CreateBackupModal
          clients={clients}
          presetClientId={createPreset}
          onClose={() => { setShowCreate(false); setCreatePreset('') }}
          onCreate={(id) => createMutation.mutate(id)}
          creating={createMutation.isLoading}
        />
      )}
      {restoreTarget && (
        <RestoreBackupModal
          backup={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onConfirm={() => restoreMutation.mutate(restoreTarget.id)}
          restoring={restoreMutation.isLoading}
        />
      )}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setBackupToDelete(null) }}
        onConfirm={() => { deleteMutation.mutate(backupToDelete.id); setShowDeleteConfirm(false); setBackupToDelete(null) }}
        isLoading={deleteMutation.isLoading}
        title="Delete backup"
        message={<div>Delete backup <span className="font-medium">{backupToDelete?.fileName}</span>? This permanently removes the backup file and can't be undone.</div>}
        confirmText="Delete backup"
        confirmVariant="destructive"
        icon={Trash2}
      />
    </div>
  )
}

export default AdminBackupDashboard
